/**
 * Phase 129 (Plan 129-05, D-12, D-14) — the Repositories card of the project Engineering tab.
 *
 * One card with a labelled "Linked GitHub repositories" sub-section: the project_repositories
 * rows that drive the engineering metrics. Any project team member links (immediately) and
 * unlinks (after confirmation) through the team routes; the linked list stays the existing
 * team-gated `GET /projects/:id/repositories`. Rendered only when the analytics module is on.
 *
 * Plan 129-08 (D-12) adds a second, read-only sub-section "Robot repositories": the project's
 * Robot allow-list (`bot_project_repositories`), which may name repositories that were never linked
 * as GitHub repositories (e.g. paperclip-factory). The two lists are different things and are not
 * merged. It is edited in the Robot card and shown only on an instance with the Robot add-on.
 */
import type { ReactElement } from 'react';
import { useModules } from '../../services/modulesContext';
import { useProjectRepositories } from '../../hooks/useProjectRepositories';
import { useRobotEnabled } from '../../hooks/useRobotEnabled';
import {
  useLinkProjectGithubRepository,
  useProjectGithubAvailable,
  useProjectRobotView,
  useUnlinkProjectGithubRepository,
} from '../../hooks/useProjectEngineering';
import { LinkedRepositoriesPanel } from './LinkedRepositoriesPanel';

export interface ProjectRepositoriesCardProps {
  projectId: number;
}

/** The Robot's repository list: read-only, one row per allow-listed repository. */
function RobotRepositoriesSubSection({ projectId }: { projectId: number }): ReactElement {
  const viewQ = useProjectRobotView(projectId);
  const repositories = viewQ.data?.config?.repositories ?? [];

  return (
    <div className="mt-6 border-t border-gray-100 pt-4" data-testid="robot-repositories">
      <h3 className="text-sm font-semibold text-gray-900">Robot repositories</h3>
      <p className="mt-1 text-sm text-gray-500">
        The repositories the Robot may work in for this project. This is a separate list from the
        linked GitHub repositories above and can include repositories that are not linked there (for
        example paperclip-factory). It is edited in the Robot card.
      </p>
      {viewQ.isLoading ? (
        <p className="mt-3 text-sm text-gray-400">Loading Robot repositories…</p>
      ) : viewQ.isError ? (
        <p className="mt-3 text-sm text-red-600" role="alert">
          Failed to load the Robot repositories.
        </p>
      ) : repositories.length === 0 ? (
        <p className="mt-3 text-sm text-gray-500" data-testid="robot-repositories-empty">
          The Robot lists no repository for this project yet.
        </p>
      ) : (
        <ul className="mt-3 divide-y divide-gray-100">
          {repositories.map((r) => (
            <li
              key={r.repository}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2"
              data-testid={`robot-repository-${r.repository}`}
            >
              <span className="font-mono text-sm text-gray-900">{r.repository}</span>
              {r.isMain && (
                <span className="inline-flex items-center rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700">
                  Main
                </span>
              )}
              <span className="text-xs text-gray-500">{`Factory™ project ${r.paperclipProjectId}`}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function ProjectRepositoriesCard({ projectId }: ProjectRepositoriesCardProps): ReactElement | null {
  const { hasModule } = useModules();
  const analyticsOn = hasModule('analytics');
  const robotEnabled = useRobotEnabled();

  const linkedQ = useProjectRepositories(analyticsOn ? projectId : undefined);
  const availableQ = useProjectGithubAvailable(projectId, analyticsOn);
  const linkM = useLinkProjectGithubRepository(projectId);
  const unlinkM = useUnlinkProjectGithubRepository(projectId);

  if (!analyticsOn) return null;

  return (
    <section
      className="bg-white rounded-lg border border-gray-200 p-6"
      data-section="engineering-repositories"
    >
      <h2 className="text-lg font-semibold text-gray-900">Repositories</h2>
      <div className="mt-4">
        <LinkedRepositoriesPanel
          embedded
          title="Linked GitHub repositories"
          description="Commits in these repositories count toward this project in the engineering metrics. A repository can be linked to more than one project."
          linked={linkedQ.data}
          available={availableQ.data}
          isLoading={linkedQ.isLoading || availableQ.isLoading}
          error={(linkedQ.error ?? availableQ.error ?? linkM.error ?? unlinkM.error) as Error | null}
          onLink={(id, onSuccess) => linkM.mutate(id, { onSuccess })}
          linkPending={linkM.isPending}
          onUnlink={(id, onSuccess) => unlinkM.mutate(id, { onSuccess })}
          unlinkPending={unlinkM.isPending}
          unlinkError={unlinkM.error as Error | null}
        />
        {robotEnabled && <RobotRepositoriesSubSection projectId={projectId} />}
      </div>
    </section>
  );
}

export default ProjectRepositoriesCard;
